import { useCallback, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import EstimatorLocationMapModal from '../../components/EstimatorLocationMapModal';
import {
  estimateTransitMixerMins,
  formatTravelTime,
  TRANSIT_MIXER_AVG_KMPH,
} from '../../utils/transitMixerEta';
import {
  getLocationDistance,
  getPincodeLocations,
  getPincodeLookup,
  getPincodeStreets,
} from '../../services/GeoLocationServices';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { areasForPincode, streetsForPincodeArea } from '../../utils/pincodeLocalities';

const PINCODE_PATTERN = /^[1-9][0-9]{5}$/;

async function fetchPostalAreasFromIndiaPost(pincode) {
  const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
  if (!response.ok) {
    return [];
  }
  const data = await response.json();
  const entry = Array.isArray(data) ? data[0] : null;
  if (!entry || entry.Status !== 'Success' || !Array.isArray(entry.PostOffice)) {
    return [];
  }
  return entry.PostOffice.map((office) => ({
    name: office.Name || office.BranchType || '',
    label: [office.Name, office.District, office.State].filter(Boolean).join(', '),
    value: office.Name || office.BranchType || '',
    state: office.State || '',
    district: office.District || '',
  })).filter((item) => item.value);
}

function mergeNamedOptions(incoming, existing = []) {
  const seen = new Set();
  const merged = [];
  [...incoming, ...existing].forEach((item) => {
    const value = String(item?.value || item?.name || item?.label || '').trim();
    const label = String(item?.label || item?.name || item?.value || '').trim();
    const name = String(item?.name || item?.value || item?.label || '').trim();
    const key = value.toLowerCase();
    if (!value || !label || value.length < 2 || label.length < 2 || seen.has(key)) {
      return;
    }
    // Skip placeholder / empty-looking names
    if (/^(select|n\/a|na|null|undefined|-)$/i.test(label)) {
      return;
    }
    seen.add(key);
    merged.push({
      value,
      label,
      name: name || label,
      state: item.state || '',
    });
  });
  return merged;
}

function roundKm(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return null;
  }
  return Math.round(n * 100) / 100;
}

/** Transit-mixer ETA from one-way road km (not car/OSRM duration). */
function estimateMinsFromOneWayKm(oneWayKm) {
  return estimateTransitMixerMins(oneWayKm);
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const radius = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const OVERPASS_URLS = [
  'https://overpass-api.de/api/interpreter',
  'https://lz4.overpass-api.de/api/interpreter',
];

async function geocodeNominatim(query) {
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=in&q=${encodeURIComponent(query)}`;
  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    return null;
  }
  const data = await response.json();
  if (!data?.[0]) {
    return null;
  }
  return {
    lat: Number(data[0].lat),
    lon: Number(data[0].lon),
    display_name: data[0].display_name || '',
  };
}

async function geocodeFlexible(query) {
  const q = String(query || '').trim();
  if (!q) {
    return null;
  }
  try {
    const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=1`;
    const response = await fetch(url);
    if (response.ok) {
      const data = await response.json();
      const feature = data?.features?.[0];
      const coords = feature?.geometry?.coordinates;
      if (coords?.length >= 2) {
        const props = feature.properties || {};
        return {
          lat: Number(coords[1]),
          lon: Number(coords[0]),
          display_name: [props.name, props.city, props.state].filter(Boolean).join(', ') || q,
        };
      }
    }
  } catch (_) {
    // try nominatim next
  }
  try {
    return await geocodeNominatim(q);
  } catch (_) {
    return null;
  }
}

async function fetchStreetsNear(lat, lon, radius = 1500) {
  const query = (
    '[out:json][timeout:20];'
    + `way["highway"]["name"](around:${radius},${lat},${lon});`
    + 'out tags;'
  );
  for (const url of OVERPASS_URLS) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: new URLSearchParams({ data: query }),
      });
      if (!response.ok) {
        continue;
      }
      const data = await response.json();
      const seen = new Set();
      const streets = [];
      (data.elements || []).forEach((element) => {
        const name = String(element?.tags?.name || '').replace(/\s+/g, ' ').trim();
        const key = name.toLowerCase();
        if (!name || name.length < 3 || seen.has(key)) {
          return;
        }
        // Skip unnamed / numeric-only / service codes
        if (/^\d+$/.test(name) || /^unnamed$/i.test(name)) {
          return;
        }
        seen.add(key);
        streets.push({ name, label: name, value: name });
      });
      streets.sort((a, b) => a.label.localeCompare(b.label));
      if (streets.length) {
        return streets.slice(0, 80);
      }
    } catch (_) {
      // try next mirror
    }
  }
  return [];
}

/** Browser fallback — returns one-way km payload. */
async function clientSideOneWayKm(fromPincode, toPincode, locationQuery) {
  const fromPin = String(fromPincode || '').trim();
  const toPin = String(toPincode || '').trim();
  if (!PINCODE_PATTERN.test(fromPin) || !PINCODE_PATTERN.test(toPin)) {
    return null;
  }
  const from = await geocodeFlexible(`${fromPin}, India`);
  const toQuery = [locationQuery, toPin, 'India'].filter(Boolean).join(', ');
  const to = await geocodeFlexible(toQuery) || await geocodeFlexible(`${toPin}, India`);
  if (!from || !to) {
    return null;
  }
  try {
    const routeUrl = `https://router.project-osrm.org/route/v1/driving/${from.lon},${from.lat};${to.lon},${to.lat}?overview=false`;
    const routeRes = await fetch(routeUrl);
    if (routeRes.ok) {
      const routeData = await routeRes.json();
      const meters = routeData?.routes?.[0]?.distance;
      if (meters != null) {
        const oneWay = roundKm(meters / 1000);
        return {
          distance_one_way_km: oneWay,
          // Always TM speed — ignore OSRM car duration
          duration_mins: estimateMinsFromOneWayKm(oneWay),
          site: to,
          source: 'osrm_client',
        };
      }
    }
  } catch (_) {
    // fall through to straight-line estimate
  }
  const oneWay = roundKm(haversineKm(from.lat, from.lon, to.lat, to.lon) * 1.3);
  return {
    distance_one_way_km: oneWay,
    duration_mins: estimateMinsFromOneWayKm(oneWay),
    site: to,
    source: 'haversine_client',
  };
}

function hasUsableDistance(res) {
  const km = Number(res?.distance_one_way_km);
  return Number.isFinite(km) && km > 0;
}

/**
 * Plant (+ optional Name slot) + Address + Pincode → Area → Street → Distance (+ map).
 */
function SitePincodeLocationFields({
  cookies,
  values = {},
  onChange,
  fromPincode = '',
  showAddressLines = true,
  showPlant = true,
  nameField = null,
}) {
  const [plants, setPlants] = useState([]);
  const [plantPincodes, setPlantPincodes] = useState({});
  const [locationOptions, setLocationOptions] = useState([]);
  const [streetOptions, setStreetOptions] = useState([]);
  const [streetsLoading, setStreetsLoading] = useState(false);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [siteCoords, setSiteCoords] = useState(null);
  const [plantCoords, setPlantCoords] = useState(null);
  const locationOptionsRef = useRef([]);
  const streetOptionsRef = useRef([]);
  const streetsLoadedKeyRef = useRef('');
  const plantAutoSetRef = useRef(false);
  const distanceReqRef = useRef(0);
  const onChangeRef = useRef(onChange);
  const mapOpenRef = useRef(false);
  const lastDistanceQueryRef = useRef('');
  const plantPincodesRef = useRef({});

  onChangeRef.current = onChange;
  mapOpenRef.current = mapOpen;
  plantPincodesRef.current = plantPincodes;

  // Stable patch — parent often passes inline onChange which would retrigger effects.
  const patch = useCallback((updates) => {
    if (typeof onChangeRef.current === 'function') {
      onChangeRef.current(updates);
    }
  }, []);

  const resolvedFromPin = (() => {
    if (fromPincode && PINCODE_PATTERN.test(String(fromPincode).trim())) {
      return String(fromPincode).trim();
    }
    const fromPlant = plantPincodes[String(values.plant_id || '')];
    if (fromPlant && PINCODE_PATTERN.test(String(fromPlant).trim())) {
      return String(fromPlant).trim();
    }
    return '';
  })();

  useEffect(() => {
    getUserAllowedPlants(cookies)
      .then((res) => {
        const list = res?.plant_list || (Array.isArray(res) ? res : []);
        const opts = list
          .filter((p) => p.status)
          .map((p) => ({
            value: String(p.id),
            label: p.plant_alias || p.entity_name || p.name || `Plant ${p.id}`,
          }));
        const pins = {};
        list.forEach((p) => {
          pins[String(p.id)] = String(p.pincode || '').trim();
        });
        setPlants(opts);
        setPlantPincodes(pins);
        plantPincodesRef.current = pins;
        if (!plantAutoSetRef.current && !values.plant_id && opts.length === 1) {
          plantAutoSetRef.current = true;
          patch({ plant_id: opts[0].value });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookies, patch]);

  // Keep TM ETA in sync with Up & Down km (recalc when old car-based 30 min is still stored).
  useEffect(() => {
    const upDown = Number(values.radius_distance) || 0;
    if (upDown <= 0) {
      return;
    }
    const mins = estimateMinsFromOneWayKm(upDown / 2);
    if (mins != null && Number(values.estimated_travel_mins) !== mins) {
      patch({ estimated_travel_mins: mins });
    }
  }, [patch, values.radius_distance, values.estimated_travel_mins]);

  const shortAreaName = useCallback((areaValue) => {
    const label = String(areaValue || '').trim();
    if (!label || label.toLowerCase() === 'map pin') {
      return '';
    }
    const match = locationOptionsRef.current.find(
      (opt) => opt.value === label || opt.label === label || opt.name === label,
    );
    if (match?.name) {
      return match.name;
    }
    return label.split(',')[0].trim();
  }, []);

  const applyPostalAreas = useCallback((pincode, offices) => {
    const curated = areasForPincode(pincode);
    const incoming = [
      ...curated,
      ...(offices || []).map((item) => ({
        value: item.name || item.value || item.label,
        label: item.label || item.name || item.value,
        name: item.name || item.label || item.value,
        state: item.state || '',
      })),
    ];
    const merged = mergeNamedOptions(incoming, locationOptionsRef.current);
    locationOptionsRef.current = merged;
    setLocationOptions(merged);

    const currentPin = String(values.site_pincode || '').trim();
    if (currentPin !== String(pincode)) {
      return;
    }
    const keepCurrent = Boolean(values.site_area);
    const nextArea = keepCurrent ? values.site_area : (merged[0]?.value || '');
    patch({
      site_area: nextArea,
      site_street: keepCurrent ? values.site_street : '',
    });
  }, [patch, values.site_area, values.site_pincode, values.site_street]);

  const applyStreets = useCallback((pincode, locality, streets) => {
    const incoming = [
      ...streetsForPincodeArea(pincode, locality),
      ...(streets || []).map((item) => ({
        value: item.name || item.value || item.label,
        label: item.label || item.name || item.value,
        name: item.name || item.label || item.value,
      })),
    ];
    const merged = mergeNamedOptions(incoming, streetOptionsRef.current);
    streetOptionsRef.current = merged;
    setStreetOptions(merged);
    setStreetsLoading(false);
  }, []);

  const loadPincodeAreas = useCallback((pincode) => {
    fetchPostalAreasFromIndiaPost(pincode)
      .then((offices) => applyPostalAreas(pincode, offices))
      .catch(() => {});
    getPincodeLocations(cookies, pincode)
      .then((res) => {
        if (res?.locations?.length) {
          applyPostalAreas(pincode, res.locations);
        }
      })
      .catch(() => {});
  }, [applyPostalAreas, cookies]);

  const loadAreaStreets = useCallback((pincode, locality) => {
    if (!pincode || !locality) {
      streetOptionsRef.current = [];
      setStreetOptions([]);
      setStreetsLoading(false);
      return;
    }

    const areaName = String(locality).split(',')[0].trim() || locality;
    setStreetsLoading(true);
    // Keep previous street list visible while reloading (avoids blank flash).

    const curated = streetsForPincodeArea(pincode, areaName);
    if (curated.length) {
      applyStreets(pincode, areaName, []);
    }

    const finishEmpty = () => {
      setStreetsLoading(false);
    };

    const loadFromMap = () => geocodeFlexible(`${areaName}, ${pincode}, Tamil Nadu, India`)
      .then((geo) => geo || geocodeFlexible(`${areaName}, ${pincode}, India`))
      .then((geo) => geo || geocodeFlexible(`${pincode}, India`))
      .then(async (geo) => {
        if (!geo) {
          finishEmpty();
          return;
        }
        if (!mapOpenRef.current) {
          setSiteCoords({ lat: geo.lat, lon: geo.lon });
        }
        const streets = await fetchStreetsNear(geo.lat, geo.lon);
        if (streets.length) {
          applyStreets(pincode, areaName, streets);
        } else {
          finishEmpty();
        }
      })
      .catch(() => finishEmpty());

    getPincodeStreets(cookies, pincode, areaName)
      .then((res) => {
        if (res?.streets?.length) {
          applyStreets(pincode, areaName, res.streets);
          return null;
        }
        return loadFromMap();
      })
      .catch(() => loadFromMap());
  }, [applyStreets, cookies]);

  const applyDistanceResponse = useCallback((res, pin) => {
    if (String(values.site_pincode || '').trim() !== pin) {
      return;
    }
    const oneWay = roundKm(res?.distance_one_way_km);
    const updates = {};
    // Field is labeled Up & Down (round trip).
    if (oneWay != null && oneWay > 0) {
      const next = roundKm(oneWay * 2);
      if (Number(values.radius_distance) !== next) {
        updates.radius_distance = next;
      }
      const mins = estimateMinsFromOneWayKm(oneWay);
      if (mins != null && Number(values.estimated_travel_mins) !== mins) {
        updates.estimated_travel_mins = mins;
      }
    }
    if (!mapOpenRef.current) {
      if (res?.site?.lat != null && res?.site?.lon != null) {
        setSiteCoords({ lat: Number(res.site.lat), lon: Number(res.site.lon) });
      } else if (res?.lat != null && res?.lon != null) {
        setSiteCoords({ lat: Number(res.lat), lon: Number(res.lon) });
      }
    }
    if (Object.keys(updates).length) {
      patch(updates);
    }
  }, [patch, values.estimated_travel_mins, values.radius_distance, values.site_pincode]);

  const lookupDistance = useCallback((query) => {
    const pin = String(values.site_pincode || '').trim();
    const plantId = String(values.plant_id || '').trim();
    const fromPin = resolvedFromPin || plantPincodesRef.current[String(plantId)] || '';
    if (!PINCODE_PATTERN.test(pin)) {
      return;
    }
    if (!fromPin && !plantId) {
      return;
    }

    const locationQuery = String(query || '').trim();
    const dedupeKey = `${plantId}|${fromPin}|${pin}|${locationQuery}`;
    if (lastDistanceQueryRef.current === dedupeKey && Number(values.radius_distance) > 0) {
      return;
    }
    lastDistanceQueryRef.current = dedupeKey;

    const reqId = ++distanceReqRef.current;
    setDistanceLoading(true);

    const params = {
      plant_id: plantId,
      from_pincode: fromPin || '',
      pincode: pin,
    };

    const backendLookup = () => {
      const primary = locationQuery
        ? getLocationDistance(cookies, { ...params, query: locationQuery })
        : getPincodeLookup(cookies, params);
      return primary.then((res) => {
        if (hasUsableDistance(res)) {
          return res;
        }
        if (locationQuery) {
          return getPincodeLookup(cookies, { ...params, location: locationQuery })
            .then((fallback) => (hasUsableDistance(fallback) ? fallback : Promise.reject(new Error('no distance'))));
        }
        return Promise.reject(new Error('no distance'));
      });
    };

    const request = PINCODE_PATTERN.test(fromPin)
      ? clientSideOneWayKm(fromPin, pin, locationQuery)
        .then((res) => (hasUsableDistance(res) ? res : Promise.reject(new Error('client miss'))))
        .catch(() => backendLookup())
        .catch(() => clientSideOneWayKm(fromPin, pin, locationQuery || pin))
      : backendLookup().catch(() => null);

    request
      .then((res) => {
        if (reqId !== distanceReqRef.current) {
          return;
        }
        if (hasUsableDistance(res)) {
          applyDistanceResponse(res, pin);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (reqId === distanceReqRef.current) {
          setDistanceLoading(false);
        }
      });
  }, [applyDistanceResponse, cookies, resolvedFromPin, values.plant_id, values.radius_distance, values.site_pincode]);

  // Recalculate when plant / area / street changes (stable deps — no function identity churn).
  useEffect(() => {
    lastDistanceQueryRef.current = '';
    const pin = String(values.site_pincode || '').trim();
    const plantId = String(values.plant_id || '').trim();
    const fromPin = resolvedFromPin || plantPincodesRef.current[plantId] || '';
    if (!plantId || !PINCODE_PATTERN.test(fromPin) || !PINCODE_PATTERN.test(pin)) {
      return undefined;
    }
    const street = String(values.site_street || '').trim();
    const areaRaw = String(values.site_area || '').trim();
    const area = areaRaw.split(',')[0].trim();
    if (!street && !area) {
      return undefined;
    }
    const timer = setTimeout(() => {
      lookupDistance([street, area].filter(Boolean).join(', '));
    }, street ? 500 : 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.plant_id, values.site_pincode, values.site_area, values.site_street, resolvedFromPin]);

  useEffect(() => {
    const pin = String(values.site_pincode || '').trim();
    if (!PINCODE_PATTERN.test(pin)) {
      locationOptionsRef.current = [];
      streetOptionsRef.current = [];
      setLocationOptions([]);
      setStreetOptions([]);
      setSiteCoords(null);
      return undefined;
    }
    locationOptionsRef.current = [];
    streetOptionsRef.current = [];
    streetsLoadedKeyRef.current = '';
    setLocationOptions([]);
    setStreetOptions([]);
    loadPincodeAreas(pin);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.site_pincode]);

  useEffect(() => {
    const pin = String(values.site_pincode || '').trim();
    const loc = shortAreaName(values.site_area) || String(values.site_area || '').trim();
    if (!PINCODE_PATTERN.test(pin) || !loc) {
      streetsLoadedKeyRef.current = '';
      setStreetOptions([]);
      return;
    }
    const key = `${pin}|${loc}`;
    if (streetsLoadedKeyRef.current === key) {
      return;
    }
    streetsLoadedKeyRef.current = key;
    loadAreaStreets(pin, loc);
  }, [values.site_pincode, values.site_area, loadAreaStreets, shortAreaName]);

  const handlePincodeChange = (event) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 6);
    if (value.length < 6) {
      locationOptionsRef.current = [];
      streetOptionsRef.current = [];
      setLocationOptions([]);
      setStreetOptions([]);
      setSiteCoords(null);
    }
    patch({
      site_pincode: value,
      site_area: '',
      site_street: '',
      radius_distance: 0,
      estimated_travel_mins: 0,
    });
    lastDistanceQueryRef.current = '';
    streetsLoadedKeyRef.current = '';
  };

  const onPlantChange = (event) => {
    const plantId = event.target.value;
    const plantPin = plantPincodes[String(plantId)] || '';
    patch({ plant_id: plantId });
    if (plantId && !PINCODE_PATTERN.test(plantPin)) {
      Swal.fire('Plant pincode missing', 'Selected plant has no valid pincode. Update the plant master so distance can be calculated.', 'warning');
    }
  };

  const onAreaSelect = (event) => {
    const label = event.target.value;
    const area = shortAreaName(label) || label;
    streetOptionsRef.current = [];
    streetsLoadedKeyRef.current = '';
    setStreetOptions([]);
    patch({
      site_area: label,
      site_street: '',
    });
    if (label && PINCODE_PATTERN.test(String(values.site_pincode || '').trim())) {
      lookupDistance(area);
    }
  };

  const onStreetSelect = (event) => {
    const street = event.target.value;
    const area = shortAreaName(values.site_area);
    const updates = {
      site_street: street,
    };
    if (street) {
      updates.address_1 = street;
      if (area && !values.address_2) {
        updates.address_2 = area;
      }
    }
    patch(updates);
    if (!street || !PINCODE_PATTERN.test(String(values.site_pincode || '').trim())) {
      return;
    }
    if (!values.plant_id) {
      Swal.fire('Plant needed', 'Select a plant first so Distance (KM) - Up & Down can be calculated.', 'info');
      return;
    }
    const query = [street, area, values.site_pincode].filter(Boolean).join(', ');
    lookupDistance(query);
  };

  const openLocationMap = () => {
    if (!values.plant_id) {
      Swal.fire('Plant needed', 'Select a plant first so distance can be calculated from plant to site.', 'info');
      return;
    }
    const pin = String(values.site_pincode || '').trim();
    const fromPin = resolvedFromPin || plantPincodes[String(values.plant_id || '')] || '';
    if (!PINCODE_PATTERN.test(pin) && !siteCoords) {
      Swal.fire('Pincode needed', 'Enter a valid 6-digit pincode first, then adjust the location on the map.', 'info');
      return;
    }

    const area = shortAreaName(values.site_area);
    const street = String(values.site_street || '').trim();
    const siteQuery = [street, area, pin, 'India'].filter(Boolean).join(', ');

    setDistanceLoading(true);
    Promise.all([
      PINCODE_PATTERN.test(fromPin) ? geocodeFlexible(`${fromPin}, India`) : Promise.resolve(null),
      siteCoords
        ? Promise.resolve(siteCoords)
        : geocodeFlexible(siteQuery).then((geo) => geo || geocodeFlexible(`${pin}, India`)),
    ])
      .then(([plantGeo, siteGeo]) => {
        if (plantGeo) {
          setPlantCoords(plantGeo);
        }
        if (siteGeo) {
          setSiteCoords(siteGeo);
        }
        setMapOpen(true);
      })
      .catch(() => setMapOpen(true))
      .finally(() => setDistanceLoading(false));
  };

  const handleMapSelect = (selection) => {
    setMapOpen(false);
    const pin = String(values.site_pincode || '').trim();
    const fromPin = resolvedFromPin || plantPincodes[String(values.plant_id || '')] || '';
    const reqId = ++distanceReqRef.current;

    // Prefer route distance from map; ETA always from transit-mixer speed.
    if (selection.route_distance_km > 0) {
      const oneWay = roundKm(selection.route_distance_km);
      patch({
        radius_distance: roundKm(oneWay * 2),
        estimated_travel_mins: estimateMinsFromOneWayKm(oneWay),
      });
      if (selection.lat != null && selection.lon != null) {
        setSiteCoords({ lat: Number(selection.lat), lon: Number(selection.lon) });
      }
      return;
    }

    setDistanceLoading(true);

    const applyMapDistance = (res) => {
      if (reqId !== distanceReqRef.current) {
        return;
      }
      const updates = {};
      if (selection.site_location && String(selection.site_location).toLowerCase() !== 'map pin') {
        updates.site_area = selection.site_location;
      }
      const oneWay = roundKm(res?.distance_one_way_km);
      if (oneWay != null && oneWay > 0) {
        updates.radius_distance = roundKm(oneWay * 2);
        updates.estimated_travel_mins = estimateMinsFromOneWayKm(oneWay);
      }
      if (selection.lat != null && selection.lon != null) {
        setSiteCoords({ lat: Number(selection.lat), lon: Number(selection.lon) });
      }
      if (Object.keys(updates).length) {
        patch(updates);
      }
    };

    getLocationDistance(cookies, {
      plant_id: values.plant_id || '',
      from_pincode: fromPin || '',
      pincode: pin,
      lat: selection.lat,
      lon: selection.lon,
      query: selection.site_location || values.site_street || shortAreaName(values.site_area),
    })
      .then((res) => {
        if (hasUsableDistance(res)) {
          applyMapDistance(res);
          return null;
        }
        return Promise.reject(new Error('no distance'));
      })
      .catch(() => {
        if (!PINCODE_PATTERN.test(fromPin)) {
          if (selection.lat != null && selection.lon != null) {
            setSiteCoords({ lat: Number(selection.lat), lon: Number(selection.lon) });
          }
          return null;
        }
        return clientSideOneWayKm(
          fromPin,
          pin,
          selection.site_location || values.site_street || shortAreaName(values.site_area),
        ).then((res) => {
          if (hasUsableDistance(res)) {
            applyMapDistance(res);
          }
        });
      })
      .finally(() => {
        if (reqId === distanceReqRef.current) {
          setDistanceLoading(false);
        }
      });
  };

  const pinValid = PINCODE_PATTERN.test(String(values.site_pincode || '').trim());
  const areaSelected = Boolean(String(values.site_area || '').trim());

  return (
    <>
      {showPlant ? (
        <>
          <label htmlFor="plant_id" className="form-group col-sm-4 text-right">Plant</label>
          <select
            id="plant_id"
            name="plant_id"
            className="browser-default custom-select form-control col-sm-7 mandatory-form-control"
            value={values.plant_id || ''}
            onChange={onPlantChange}
          >
            <option value="">Select Plant</option>
            {plants.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </>
      ) : null}

      {nameField}

      {showAddressLines ? (
        <>
          <label htmlFor="address_1" className="form-group col-sm-4 text-right">Address </label>
          <input type="text" id="address_1" name="address_1" onChange={(e) => patch({ address_1: e.target.value })} value={values.address_1 || ''} className="form-control col-sm-7" />

          <label htmlFor="address_2" className="form-group col-sm-4 text-right" />
          <input type="text" id="address_2" name="address_2" onChange={(e) => patch({ address_2: e.target.value })} value={values.address_2 || ''} className="form-control col-sm-7" />

          <label htmlFor="address_3" className="form-group col-sm-4 text-right" />
          <input type="text" id="address_3" name="address_3" onChange={(e) => patch({ address_3: e.target.value })} value={values.address_3 || ''} className="form-control col-sm-7" />
        </>
      ) : null}

      <label htmlFor="site_pincode" className="form-group col-sm-4 text-right">Pincode</label>
      <input
        name="site_pincode"
        id="site_pincode"
        onChange={handlePincodeChange}
        value={values.site_pincode || ''}
        className="form-control col-sm-7"
        type="text"
        maxLength="6"
        placeholder="6-digit pincode"
      />

      {pinValid ? (
        <>
          <label htmlFor="site_area" className="form-group col-sm-4 text-right">Area</label>
          <select
            id="site_area"
            name="site_area"
            className="browser-default custom-select form-control col-sm-7"
            value={values.site_area || ''}
            onChange={onAreaSelect}
          >
            <option value="">{locationOptions.length ? 'Select area' : 'Loading areas…'}</option>
            {locationOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
            {values.site_area && !locationOptions.some((opt) => opt.value === values.site_area) ? (
              <option value={values.site_area}>{values.site_area}</option>
            ) : null}
          </select>
        </>
      ) : null}

      {pinValid && areaSelected ? (
        <>
          <label htmlFor="site_street" className="form-group col-sm-4 text-right">Street</label>
          {streetOptions.length || streetsLoading ? (
            <select
              id="site_street"
              name="site_street"
              className="browser-default custom-select form-control col-sm-7"
              value={values.site_street || ''}
              onChange={onStreetSelect}
              disabled={streetsLoading}
            >
              <option value="">{streetsLoading ? 'Loading streets…' : 'Select street'}</option>
              {streetOptions
                .filter((opt) => String(opt?.value || '').trim() && String(opt?.label || '').trim())
                .map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              {values.site_street
                && String(values.site_street).trim()
                && !streetOptions.some((opt) => opt.value === values.site_street) ? (
                <option value={values.site_street}>{values.site_street}</option>
              ) : null}
            </select>
          ) : (
            <input
              type="text"
              id="site_street"
              name="site_street"
              className="form-control col-sm-7"
              value={values.site_street || ''}
              onChange={onStreetSelect}
              placeholder="Enter street / landmark"
            />
          )}
        </>
      ) : null}

      <label htmlFor="radius_distance" className="form-group col-sm-4 text-right">Distance (KM) - Up &amp; Down</label>
      <input
        type="number"
        id="radius_distance"
        name="radius_distance"
        step="any"
        onChange={(e) => {
          const upDown = e.target.value === '' ? 0 : Number(e.target.value);
          const oneWay = upDown > 0 ? upDown / 2 : 0;
          patch({
            radius_distance: upDown,
            estimated_travel_mins: oneWay > 0 ? estimateMinsFromOneWayKm(oneWay) : 0,
          });
        }}
        value={values.radius_distance || 0}
        className="form-control col-sm-4 mandatory-form-control"
        title={distanceLoading ? 'Calculating distance…' : 'Round-trip km (plant ↔ site)'}
      />
      <button
        type="button"
        className="btn btn-outline-danger btn-sm col-sm-3"
        onClick={openLocationMap}
        title="Show plant → site shortest route inside the app"
        style={{ maxWidth: '120px', opacity: distanceLoading ? 0.7 : 1 }}
      >
        Map / Route
      </button>
      {Number(values.radius_distance) > 0 ? (
        <span className="form-group col-sm-12 text-muted" style={{ fontSize: '12px', paddingLeft: '8px' }}>
          One way ~{(Number(values.radius_distance) / 2).toFixed(2)} km
          {values.estimated_travel_mins
            ? ` · TM ETA ${formatTravelTime(values.estimated_travel_mins)} (@ ~${TRANSIT_MIXER_AVG_KMPH} km/h)`
            : ''}
        </span>
      ) : null}

      <label htmlFor="estimated_travel_mins" className="form-group col-sm-4 text-right">Est. Time to Reach (TM)</label>
      <input
        type="number"
        id="estimated_travel_mins"
        name="estimated_travel_mins"
        min="0"
        step="1"
        onChange={(e) => patch({ estimated_travel_mins: e.target.value === '' ? 0 : Number(e.target.value) })}
        value={values.estimated_travel_mins || 0}
        className="form-control col-sm-4"
        title={`Transit mixer one-way ETA @ ~${TRANSIT_MIXER_AVG_KMPH} km/h (not car speed)`}
      />
      <span className="form-group col-sm-3 text-muted" style={{ paddingTop: '6px', fontSize: '12px' }}>
        {formatTravelTime(values.estimated_travel_mins) || 'mins'}
        {' '}
        <span title={`Loaded transit mixer @ ~${TRANSIT_MIXER_AVG_KMPH} km/h`}>TM</span>
      </span>

      <EstimatorLocationMapModal
        open={mapOpen}
        title="Shortest route: Plant → Site"
        plant={mapOpen ? plantCoords : null}
        site={mapOpen ? siteCoords : null}
        selectedLabel={values.site_street || values.site_area || ''}
        mapsUrl={(
          (() => {
            const fromPin = resolvedFromPin || plantPincodes[String(values.plant_id || '')] || '';
            const pin = String(values.site_pincode || '').trim();
            if (!PINCODE_PATTERN.test(fromPin) || !PINCODE_PATTERN.test(pin)) {
              return '';
            }
            const area = shortAreaName(values.site_area);
            const street = String(values.site_street || '').trim();
            const destination = encodeURIComponent([street, area, pin, 'India'].filter(Boolean).join(', '));
            const origin = encodeURIComponent(`${fromPin}, India`);
            return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
          })()
        )}
        onClose={() => setMapOpen(false)}
        onSelect={handleMapSelect}
      />
    </>
  );
}

export default SitePincodeLocationFields;
export { PINCODE_PATTERN };
